/**
 * DocManagement.tsx — Rich document management for DealDetail Documents tab.
 *
 * Features:
 * - Signature status pills + signer chips
 * - Three-dot menu: View, Download, Rename, Manage signers, Sign, Delete
 * - Manage signers sheet: add/remove internal friends or external parties as signers
 * - Inline rename
 * - Mobile: card view below md, bottom-sheet menus
 */

import { useState, useEffect, useCallback } from 'react';
import { Stack, Row, Text, Button, Tag, Badge, MobileSheet, EmptyState, Avatar, Input } from '../ui';
import { api } from '../api/client';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ardacious.app';

const SIG_STATUS: Record<string, { label: string; tone: 'neutral' | 'positive' | 'warning' | 'negative' }> = {
  none:    { label: 'unsigned',  tone: 'neutral' },
  pending: { label: 'needs signature', tone: 'warning' },
  partial: { label: 'partial',   tone: 'warning' },
  signed:  { label: 'signed',    tone: 'positive' },
};

const REVIEW_DOT: Record<string, string> = {
  green: 'bg-positive', red: 'bg-negative', yellow: 'bg-amber-400',
  error: 'bg-negative', pending: 'bg-muted',
};

// ── Manage Signers Sheet ──────────────────────────────────────────────────────

function ManageSignersSheet({
  artifact,
  open,
  onClose,
  friends,
  parties,
  onChanged,
}: {
  artifact: any;
  open: boolean;
  onClose: () => void;
  friends: any[];
  parties: any[];
  onChanged: () => void;
}) {
  const [signers, setSigners] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<'members' | 'parties'>('members');
  const [label, setLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const loadSigners = useCallback(async () => {
    if (!artifact) return;
    setLoading(true);
    try {
      const r = await api.getArtifactSigners(artifact.id);
      setSigners(r.signers ?? []);
    } catch {} finally { setLoading(false); }
  }, [artifact]);

  useEffect(() => { if (open) loadSigners(); }, [open, loadSigners]);

  async function addFriend(f: any) {
    setAdding(true);
    try {
      await api.assignSigners(artifact.id, [{ kind: 'friend', friendId: f.id, required: true, label: label || undefined }]);
      setLabel('');
      loadSigners();
      onChanged();
    } catch (e: any) { alert(e.message); }
    finally { setAdding(false); }
  }

  async function addParty(p: any) {
    setAdding(true);
    try {
      await api.assignSigners(artifact.id, [{ kind: 'party', partyId: p.id, required: true, label: label || undefined }]);
      setLabel('');
      loadSigners();
      onChanged();
    } catch (e: any) { alert(e.message); }
    finally { setAdding(false); }
  }

  async function removeSigner(signerId: string) {
    setRemoving(signerId);
    try {
      await api.removeArtifactSigner(artifact.id, signerId);
      loadSigners();
      onChanged();
    } catch (e: any) { alert(e.message); }
    finally { setRemoving(null); }
  }

  // Filter out already-assigned
  const assignedFriendIds = new Set(signers.filter(s => s.signerKind === 'friend').map(s => s.friendId));
  const assignedPartyIds = new Set(signers.filter(s => s.signerKind === 'party').map(s => s.partyId));

  return (
    <MobileSheet open={open} onClose={onClose} title={`Signers — ${artifact?.title ?? ''}`}>
      <Stack gap={4}>
        {/* Current signers */}
        <Stack gap={2}>
          <Text variant="micro">Assigned signers</Text>
          {loading ? <Text variant="label" muted>loading…</Text>
            : signers.length === 0 ? <Text variant="label" muted>none assigned</Text>
            : signers.map(s => (
              <Row key={s.id} gap={2} align="center" justify="between">
                <Row gap={2} align="center">
                  <Avatar name={s.name ?? '?'} size="sm" />
                  <Stack gap={0}>
                    <Text variant="small">{s.name}</Text>
                    <Row gap={1}>
                      <Text variant="label" muted>{s.signerKind}</Text>
                      {s.label && <Text variant="label" muted>· {s.label}</Text>}
                    </Row>
                  </Stack>
                </Row>
                <Row gap={1} align="center">
                  {s.signedAt
                    ? <Text variant="label" className="text-positive">✓ signed</Text>
                    : <Text variant="label" muted>pending</Text>}
                  <Button variant="ghost" size="sm" disabled={!!removing} onClick={() => removeSigner(s.id)}>
                    {removing === s.id ? '…' : 'Remove'}
                  </Button>
                </Row>
              </Row>
            ))}
        </Stack>

        {/* Add signer */}
        <Stack gap={2}>
          <Text variant="micro">Add signer</Text>
          <Stack gap={1}>
            <Text variant="label" muted>Optional label (e.g. "counter-signature")</Text>
            <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="label (optional)" />
          </Stack>
          {/* Tab switcher */}
          <div className="flex gap-0 hairline-b">
            <button onClick={() => setTab('members')} className={`px-3 py-1.5 text-xs border-b-2 -mb-px ${tab === 'members' ? 'border-ink text-ink font-medium' : 'border-transparent text-muted'}`}>
              Group members
            </button>
            <button onClick={() => setTab('parties')} className={`px-3 py-1.5 text-xs border-b-2 -mb-px ${tab === 'parties' ? 'border-ink text-ink font-medium' : 'border-transparent text-muted'}`}>
              External parties
            </button>
          </div>
          <Stack gap={1} className="max-h-48 overflow-y-auto">
            {tab === 'members' && friends.filter(f => !assignedFriendIds.has(f.id)).map(f => (
              <button key={f.id} disabled={adding}
                onClick={() => addFriend(f)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-whisper text-left transition-colors">
                <Avatar name={f.name} size="sm" />
                <Text variant="small">{f.name}</Text>
              </button>
            ))}
            {tab === 'parties' && parties.filter(p => !assignedPartyIds.has(p.id)).map(p => (
              <button key={p.id} disabled={adding}
                onClick={() => addParty(p)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-whisper text-left transition-colors">
                <span className="h-6 w-6 rounded-full bg-whisper hairline flex items-center justify-center text-[10px] font-medium shrink-0">
                  {(p.legalName ?? p.contactName ?? '?')[0]?.toUpperCase()}
                </span>
                <Stack gap={0} className="min-w-0">
                  <Text variant="small" className="truncate">{p.legalName}</Text>
                  <Text variant="label" muted>{p.role}</Text>
                </Stack>
              </button>
            ))}
            {tab === 'members' && friends.filter(f => !assignedFriendIds.has(f.id)).length === 0 && (
              <Text variant="label" muted>All group members already assigned</Text>
            )}
            {tab === 'parties' && parties.filter(p => !assignedPartyIds.has(p.id)).length === 0 && (
              <Text variant="label" muted>All external parties already assigned</Text>
            )}
          </Stack>
        </Stack>
      </Stack>
    </MobileSheet>
  );
}

// ── Sign Document Sheet ───────────────────────────────────────────────────────

function SignSheet({
  artifact,
  open,
  onClose,
  signerName: defaultName,
  onSigned,
}: {
  artifact: any;
  open: boolean;
  onClose: () => void;
  signerName: string;
  onSigned: () => void;
}) {
  const [name, setName] = useState(defaultName);
  const [signing, setSigning] = useState(false);

  async function submit() {
    if (!artifact || !name.trim()) return;
    setSigning(true);
    try {
      await api.signArtifact(artifact.id, name.trim(), name.trim());
      onSigned();
      onClose();
    } catch (e: any) { alert(e.message); }
    finally { setSigning(false); }
  }

  return (
    <MobileSheet open={open} onClose={onClose} title={`Sign: ${artifact?.title ?? ''}`}>
      <Stack gap={3}>
        <Text variant="small" muted>Type your full name to apply your signature.</Text>
        <Stack gap={1}>
          <Text variant="micro">Full name</Text>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" />
          {name.trim() && (
            <div className="hairline rounded-sm p-3 bg-whisper mt-1">
              <span style={{ fontFamily: 'Georgia, serif', fontSize: '20px', fontStyle: 'italic' }}>{name}</span>
            </div>
          )}
        </Stack>
        <Row gap={2} justify="end">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!name.trim() || signing} onClick={submit}>
            {signing ? 'Signing…' : 'Sign document'}
          </Button>
        </Row>
      </Stack>
    </MobileSheet>
  );
}

// ── Per-row three-dot menu ────────────────────────────────────────────────────

function DocMenu({
  artifact,
  onView,
  onRename,
  onManageSigners,
  onSign,
  onDelete,
}: {
  artifact: any;
  onView: () => void;
  onRename: () => void;
  onManageSigners: () => void;
  onSign: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);

  const actions = [
    { label: 'View', fn: onView },
    { label: 'Download', fn: () => { window.open(`${API_BASE}/vault/${artifact.id}/download`, '_blank'); } },
    { label: 'Rename', fn: onRename },
    { label: 'Manage signers', fn: onManageSigners },
    { label: 'Sign', fn: onSign },
    { label: 'Delete', fn: onDelete, destructive: true },
  ];

  return (
    <div className="relative">
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        className="h-7 w-7 flex items-center justify-center rounded-sm hover:bg-whisper text-muted hover:text-ink"
        aria-label="Document actions"
      >
        ···
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-40 bg-bg hairline rounded-sm shadow-lg min-w-[160px]">
            {actions.map(a => (
              <button key={a.label}
                onClick={() => { setOpen(false); a.fn(); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-whisper transition-colors ${a.destructive ? 'text-negative' : 'text-ink'}`}>
                {a.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main DocManagement Component ──────────────────────────────────────────────

export function DocManagement({
  artifacts,
  friends,
  parties,
  myName,
  onRefresh,
}: {
  artifacts: any[];
  friends: any[];
  parties: any[];
  myName: string;
  onRefresh: () => void;
}) {
  const [manageTarget, setManageTarget] = useState<any>(null);
  const [signTarget, setSignTarget] = useState<any>(null);
  const [renameTarget, setRenameTarget] = useState<any>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [viewTarget, setViewTarget] = useState<any>(null);

  async function handleDelete(a: any) {
    if (!confirm(`Delete "${a.title}"? It can be restored from Settings → Deleted documents.`)) return;
    setDeleting(a.id);
    try {
      await api.deleteArtifact(a.id);
      onRefresh();
    } catch (e: any) { alert(e.message); }
    finally { setDeleting(null); }
  }

  async function handleRename() {
    if (!renameTarget || !renameValue.trim()) return;
    setRenaming(true);
    try {
      await api.renameArtifact(renameTarget.id, renameValue.trim());
      onRefresh();
      setRenameTarget(null);
    } catch (e: any) { alert(e.message); }
    finally { setRenaming(false); }
  }

  if (artifacts.length === 0) {
    return <EmptyState title="no documents yet" hint="upload above, or ask the deal agent to draft a memo" />;
  }

  return (
    <>
      {/* Mobile card view */}
      <div className="md:hidden space-y-2">
        {artifacts.map(a => {
          const sig = SIG_STATUS[a.signatureStatus ?? 'none'];
          return (
            <div key={a.id} className={`hairline rounded-sm p-3 ${deleting === a.id ? 'opacity-40' : ''}`}>
              <Row justify="between" align="start">
                <Stack gap={0.5} className="flex-1 min-w-0">
                  <Row gap={1} align="center">
                    {a.reviewStatus && REVIEW_DOT[a.reviewStatus] && (
                      <span className={`h-2 w-2 rounded-full shrink-0 ${REVIEW_DOT[a.reviewStatus]}`} />
                    )}
                    <Text variant="small" className="font-medium truncate">{a.title}</Text>
                  </Row>
                  <Row gap={1} wrap>
                    <Tag>{a.type}</Tag>
                    <Badge tone={sig.tone}>{sig.label}</Badge>
                    {a.encryptionTier === 'tier1' && <Tag>E2EE</Tag>}
                  </Row>
                </Stack>
                <DocMenu
                  artifact={a}
                  onView={() => setViewTarget(a)}
                  onRename={() => { setRenameTarget(a); setRenameValue(a.title); }}
                  onManageSigners={() => setManageTarget(a)}
                  onSign={() => setSignTarget(a)}
                  onDelete={() => handleDelete(a)}
                />
              </Row>
            </div>
          );
        })}
      </div>

      {/* Desktop table view */}
      <div className="hidden md:block">
        <div className="hairline rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="hairline-b bg-whisper">
                <th className="text-left px-3 py-2 text-[11px] font-medium text-muted">Document</th>
                <th className="text-left px-3 py-2 text-[11px] font-medium text-muted">Type</th>
                <th className="text-left px-3 py-2 text-[11px] font-medium text-muted">Signatures</th>
                <th className="px-3 py-2 text-[11px] font-medium text-muted text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {artifacts.map((a, i) => {
                const sig = SIG_STATUS[a.signatureStatus ?? 'none'];
                return (
                  <tr key={a.id} className={`hairline-b last:border-b-0 ${i % 2 !== 0 ? 'bg-whisper/40' : ''} ${deleting === a.id ? 'opacity-40' : ''}`}>
                    <td className="px-3 py-2.5">
                      <Row gap={1} align="center">
                        {a.reviewStatus && REVIEW_DOT[a.reviewStatus] && (
                          <span className={`h-2 w-2 rounded-full shrink-0 ${REVIEW_DOT[a.reviewStatus]}`} />
                        )}
                        <Text variant="small" className="font-medium">{a.title}</Text>
                        {a.encryptionTier === 'tier1' && <Tag>E2EE</Tag>}
                      </Row>
                    </td>
                    <td className="px-3 py-2.5"><Tag>{a.type}</Tag></td>
                    <td className="px-3 py-2.5">
                      <Badge tone={sig.tone}>{sig.label}</Badge>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <DocMenu
                        artifact={a}
                        onView={() => setViewTarget(a)}
                        onRename={() => { setRenameTarget(a); setRenameValue(a.title); }}
                        onManageSigners={() => setManageTarget(a)}
                        onSign={() => setSignTarget(a)}
                        onDelete={() => handleDelete(a)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manage signers sheet */}
      <ManageSignersSheet
        artifact={manageTarget}
        open={!!manageTarget}
        onClose={() => setManageTarget(null)}
        friends={friends}
        parties={parties}
        onChanged={onRefresh}
      />

      {/* Sign sheet */}
      <SignSheet
        artifact={signTarget}
        open={!!signTarget}
        onClose={() => setSignTarget(null)}
        signerName={myName}
        onSigned={onRefresh}
      />

      {/* Rename sheet */}
      <MobileSheet open={!!renameTarget} onClose={() => setRenameTarget(null)} title="Rename document">
        <Stack gap={3}>
          <Stack gap={1}>
            <Text variant="micro">New title</Text>
            <Input
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleRename()}
              autoFocus
            />
          </Stack>
          <Row gap={2} justify="end">
            <Button variant="ghost" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button variant="primary" disabled={!renameValue.trim() || renaming} onClick={handleRename}>
              {renaming ? 'Saving…' : 'Rename'}
            </Button>
          </Row>
        </Stack>
      </MobileSheet>

      {/* View sheet */}
      <MobileSheet open={!!viewTarget} onClose={() => setViewTarget(null)} title={viewTarget?.title ?? ''} maxWidthMd="w-[700px]">
        {viewTarget && (
          <iframe
            src={`${API_BASE}/vault/${viewTarget.id}/download`}
            className="w-full h-[70vh] sm:h-[80vh] rounded-sm border-0"
            title={viewTarget.title}
          />
        )}
      </MobileSheet>
    </>
  );
}
